import { __decorate } from "tslib";
import React, { cloneElement } from 'react';
import omit from 'lodash/omit';
import isPlainObject from 'lodash/isPlainObject';
import isNil from 'lodash/isNil';
import defer from 'lodash/defer';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import isString from 'lodash/isString';
import { observer } from 'mobx-react';
import { isMoment } from 'moment';
import { action, computed, isArrayLike, observable, reaction, runInAction, toJS } from 'mobx';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import Icon from '../../icon';
import { TextField } from '../../text-field/TextField';
import autobind from '../../_util/autobind';
import { getEditorByField } from '../utils';
import ObserverSelect from '../../select/Select';
import Option from '../../option/Option';
import isSameLike from '../../_util/isSameLike';
import { DataSetEvents, FieldType } from '../../data-set/enum';
import { processFieldValue, toRangeValue, processValue } from '../../field/utils';
let FilterSelect = class FilterSelect extends TextField {
    constructor(props, context) {
        super(props, context);
        this.setFilterText = debounce(action((text) => {
            this.filterText = text;
        }), 500);
        this.doQuery = throttle(() => {
            const { onQuery = noop } = this.props;
            const { optionDataSet } = this.observableProps;
            optionDataSet.query();
            onQuery();
        }, 500);
        const { observableProps } = this;
        this.on(observableProps.queryDataSet);
        this.reaction = reaction(() => observableProps.queryDataSet, this.on);
    }
    getPrefix() {
        const { prefix = React.createElement(Icon, { type: "filter_list" }) } = this.props;
        if (prefix) {
            return this.wrapperPrefix(prefix);
        }
    }
    get value() {
        const { filter } = this.props;
        const { value, queryDataSet } = this.observableProps;
        if (value) {
            return filter ? value.filter(filter) : value;
        }
        const { paramName } = this.props;
        if (queryDataSet) {
            const { current } = queryDataSet;
            if (current) {
                const result = [];
                const keys = queryDataSet.fields.keys();
                (paramName ? [...new Set([...keys, paramName])] : [...keys]).forEach((key) => {
                    if (key && (!filter || filter(key))) {
                        const values = current.get(key);
                        if (isArrayLike(values)) {
                            const field = current.getField(key);
                            if (field && field.get('multiple', current)) {
                                values.forEach(item => !isNil(item) && result.push(key));
                            }
                            else if (values.some(item => !isNil(item))) {
                                result.push(key);
                            }
                        }
                        else if (!isNil(values)) {
                            result.push(key);
                        }
                    }
                });
                return result;
            }
        }
        return undefined;
    }
    set value(value) {
        runInAction(() => {
            this.observableProps.value = value;
        });
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            optionDataSet: props.optionDataSet,
            queryDataSet: props.queryDataSet,
        };
    }
    on(ds) {
        this.off();
        if (ds) {
            ds.addEventListener(DataSetEvents.update, this.handleDataSetUpdate);
            ds.addEventListener(DataSetEvents.reset, this.handleDataSetReset);
        }
        this.queryDataSet = ds;
    }
    off() {
        const { queryDataSet } = this;
        if (queryDataSet) {
            queryDataSet.removeEventListener(DataSetEvents.update, this.handleDataSetUpdate);
            queryDataSet.removeEventListener(DataSetEvents.reset, this.handleDataSetReset);
        }
    }
    componentWillUnmount() {
        super.componentWillUnmount();
        this.setFilterText.cancel();
        this.off();
        this.reaction();
    }
    setText(text) {
        super.setText(text);
        this.setFilterText(text);
    }
    getPlaceholders() {
        if (!this.selectField) {
            return super.getPlaceholders();
        }
        return [];
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'paramName',
            'optionDataSet',
            'queryDataSet',
            'dropdownMenuStyle',
            'hiddenIfNone',
            'editable',
            'filter',
        ]);
    }
    getRootDomNode() {
        return this.element;
    }
    defaultRenderer({ value, repeat = 0 }) {
        const { paramName } = this.props;
        const { queryDataSet } = this.observableProps;
        if (queryDataSet) {
            const { current } = queryDataSet;
            if (current) {
                let fieldValue = current.get(value);
                if (value === paramName) {
                    return fieldValue;
                }
                const field = queryDataSet.getField(value);
                if (field) {
                    const range = field.get('range', current);
                    if (field.get('multiple', current)) {
                        fieldValue = (fieldValue || [])[repeat];
                    }
                    const showInvalidDate = this.getContextConfig('showInvalidDate');
                    const processValueOptions = {
                        dateFormat: this.getDateFormat(field),
                        showInvalidDate,
                        isNumber: [FieldType.number, FieldType.currency, FieldType.bigNumber].includes(field.get('type', current)),
                        precision: field && field.get('precision', current),
                        useZeroFilledDecimal: this.getContextConfig('useZeroFilledDecimal'),
                    };
                    if (range) {
                        return `${this.getFieldLabel(field, current)}: ${toRangeValue(fieldValue, range).map(v => {
                            return processFieldValue(isPlainObject(v) ? v : processValue(v, processValueOptions), field, {
                                getProp: (name) => this.getProp(name),
                                getValue: () => this.getValue(),
                                lang: this.lang,
                                getDisplayProp: (name) => this.getDisplayProp(name),
                            }, undefined, current, this.getContextConfig);
                        }).join(this.rangeSeparator)}`;
                    }
                    if (field.get('bind', current) || isNil(fieldValue))
                        return;
                    const text = this.processText(isNil(fieldValue)
                        ? processValue(value, processValueOptions)
                        : isMoment(fieldValue) ? processValue(fieldValue, processValueOptions) : fieldValue);
                    return `${this.getFieldLabel(field, current)}: ${processFieldValue(isPlainObject(fieldValue) ? fieldValue : text, field, {
                        getProp: (name) => this.getProp(name),
                        getValue: () => this.getValue(),
                        lang: this.lang,
                        getDisplayProp: (name) => this.getDisplayProp(name),
                    }, undefined, current, this.getContextConfig)}`;
                }
                return value;
            }
        }
    }
    getQueryRecord() {
        const { queryDataSet } = this.observableProps;
        if (queryDataSet) {
            return queryDataSet.current;
        }
    }
    getQueryField(fieldName) {
        const { queryDataSet } = this.observableProps;
        if (queryDataSet) {
            return queryDataSet.getField(fieldName);
        }
    }
    addQueryParams(value) {
        const { paramName } = this.props;
        if (paramName) {
            this.setQueryValue(paramName, value);
        }
    }
    afterRemoveValue(value, repeat) {
        const values = this.getQueryValues(value);
        if (repeat === -1) {
            values.pop();
        }
        else {
            values.splice(repeat, 1);
        }
        const multiple = this.getQueryFieldMultiple(value);
        this.setQueryValue(value, multiple ? values : values[0]);
    }
    getQueryFieldMultiple(value) {
        const { paramName } = this.props;
        if (paramName !== value) {
            const field = this.getQueryField(value);
            if (field && field.get('multiple', this.getQueryRecord())) {
                return true;
            }
        }
        return false;
    }
    handleDataSetReset() {
        const { onReset = noop } = this.props;
        this.setValue(undefined);
        onReset();
    }
    handleDataSetUpdate({ name, value, record }) {
        const values = this.getValues();
        if (isArrayLike(value)) {
            const { length } = value;
            if (length) {
                const field = record.getField(name);
                if (field && field.get('multiple', record)) {
                    let repeat = 0;
                    const filtered = values.filter(item => {
                        if (item === name) {
                            repeat += 1;
                            if (repeat > length) {
                                return false;
                            }
                        }
                        return true;
                    });
                    for (let i = 0, n = length - repeat; i < n; i += 1) {
                        filtered.push(name);
                    }
                    this.setValue(filtered);
                }
                else if (values.indexOf(name) === -1) {
                    values.push(name);
                    this.setValue(values);
                }
            }
            else {
                this.setValue(values.filter(item => item !== name));
            }
        }
        else if (isNil(value)) {
            this.setValue(values.filter(item => item !== name));
        }
        else {
            if (values.indexOf(name) === -1) {
                values.push(name);
            }
            this.setValue(values);
        }
        if (!this.isDoClear) {
            this.doQuery();
        }
    }
    handleBlur(e) {
        super.handleBlur(e);
        if (!e.isDefaultPrevented()) {
            this.setSelectField(undefined);
        }
    }
    isEditorReadOnly() {
        const { paramName, editable } = this.props;
        return (this.getQueryValues(paramName).length > 0 && !this.selectField) || !editable;
    }
    handleFieldChange(value) {
        const { selectField } = this;
        if (selectField) {
            const { name } = selectField;
            this.setQueryValue(name, value);
        }
        else if (isString(value)) {
            this.addQueryParams(value);
            if (this.isFocused) {
                this.element.expand();
            }
        }
        else {
            this.setSelectField(value);
        }
    }
    handleInput(e) {
        this.setText(e.target.value);
    }
    handleFieldEnterDown() {
        defer(() => this.focus());
    }
    handleKeyDown(e) {
        if (this.selectField) {
            if (e.keyCode === KeyCode.BACKSPACE && !this.text) {
                this.setSelectField(undefined);
            }
        }
        else {
            super.handleKeyDown(e);
        }
    }
    handleEnterDown() {
        // noop
    }
    setSelectField(value) {
        this.selectField = value;
        this.setFilterText(undefined);
    }
    getQueryValues(fieldName) {
        const current = this.getQueryRecord();
        const field = this.getQueryField(fieldName);
        const range = field && field.get('range', current);
        if (current) {
            const value = toJS(current.get(fieldName)) || [];
            return [].concat(!range ? value : [value]);
        }
        return [];
    }
    syncValueOnBlur() {
        // noop
    }
    setQueryValue(fieldName, value) {
        const current = this.getQueryRecord();
        if (current) {
            current.set(fieldName, value);
        }
        this.setSelectField(undefined);
    }
    getFieldLabel(field, record) {
        return field.get('label', record) || field.name;
    }
    multipleFieldExistsValue(field, current) {
        if (field.get('multiple', current)) {
            const options = field.getOptions(current);
            if (options && current) {
                const values = current.get(field.name);
                const valueField = field.get('valueField', current);
                return options.some(r => !values.some(value => isSameLike(r.get(valueField), value)));
            }
        }
        return false;
    }
    getInputFilterOptions(filterText) {
        const { optionDataSet, optionDataSet: { fields }, } = this.observableProps;
        const values = new Set();
        optionDataSet.forEach(record => {
            fields.forEach((_, key) => {
                const value = record.get(key);
                if (isString(value) && value.toLowerCase().indexOf(filterText.toLowerCase()) !== -1) {
                    values.add(value);
                }
            });
        });
        return [...values].map(value => (React.createElement(Option, { key: value, value: value }, value)));
    }
    getFieldSelectOptions() {
        const { paramName } = this.props;
        const { queryDataSet } = this.observableProps;
        const data = [];
        if (queryDataSet) {
            const { current } = queryDataSet;
            queryDataSet.fields.forEach((field, key) => {
                if (key !== paramName &&
                    (this.getValues().indexOf(key) === -1 ||
                        this.multipleFieldExistsValue(field, current)) &&
                    !field.get('bind', current)) {
                    data.push(React.createElement(Option, { key: String(key), value: field }, this.getFieldLabel(field, current)));
                }
            });
        }
        return data;
    }
    getFieldEditor(props, selectField) {
        const current = this.queryDataSet ? this.queryDataSet.current : undefined;
        const editor = getEditorByField(selectField, current, true);
        const record = this.getQueryRecord();
        const { editorProps = noop } = this.props;
        const editorFunProps = editorProps({ name: selectField.name, record, editor });
        const editorMergeProps = {
            ...editorFunProps,
            ...props,
            key: 'value',
            record,
            name: selectField.name,
            autoFocus: true,
            onInput: this.handleInput,
            onEnterDown: this.handleFieldEnterDown,
            renderer: noop,
            isFlat: true,
        };
        if (editor.type.__PRO_SELECT) {
            editorMergeProps.dropdownMenuStyle = this.props.dropdownMenuStyle;
            editorMergeProps.dropdownMatchSelectWidth = false;
        }
        return cloneElement(editor, editorMergeProps);
    }
    getFieldSelect(props) {
        const { filterText, props: { dropdownMenuStyle }, } = this;
        const editable = !this.isEditorReadOnly();
        const options = editable && filterText
            ? this.getInputFilterOptions(filterText)
            : this.getFieldSelectOptions();
        return (React.createElement(ObserverSelect, Object.assign({}, props, { key: "key", combo: editable, searchable: editable, value: null, onInput: this.handleInput, onEnterDown: this.handleFieldEnterDown, autoFocus: this.isFocused, dropdownMenuStyle: dropdownMenuStyle, dropdownMatchSelectWidth: false }), options));
    }
    clear() {
        const record = this.getQueryRecord();
        this.isDoClear = true;
        if (record) {
            record.clear();
            this.doQuery();
        }
        this.setValue(undefined);
        this.setSelectField(undefined);
        this.element.text = undefined;
        this.isDoClear = false;
    }
    renderWrapper() {
        const { hiddenIfNone } = this.props;
        if (this.isEmpty() && hiddenIfNone) {
            return null;
        }
        return super.renderWrapper();
    }
    renderMultipleEditor(props) {
        const { selectField, prefixCls } = this;
        const editorProps = {
            ...omit(props, ['multiple', 'prefixCls']),
            clearButton: false,
            prefix: null,
            suffix: null,
            elementClassName: `${prefixCls}-inner-editor`,
            onChange: this.handleFieldChange,
        };
        editorProps.style = { width: 'auto' };
        return (React.createElement("li", { key: "text" },
            selectField ? (React.createElement("span", { className: `${prefixCls}-select-field` },
                this.getFieldLabel(selectField, this.getQueryRecord()),
                ":")) : null,
            selectField
                ? this.getFieldEditor(editorProps, selectField)
                : this.getFieldSelect(editorProps)));
    }
    handleMouseDown(e) {
        if (e.target !== this.element) {
            if (!this.isFocused) {
                this.focus();
            }
        }
    }
};
FilterSelect.displayName = 'FilterSelect';
FilterSelect.defaultProps = {
    ...TextField.defaultProps,
    multiple: true,
    clearButton: true,
    editable: true,
    dropdownMenuStyle: { minWidth: pxToRem(180) },
};
__decorate([
    observable
], FilterSelect.prototype, "selectField", void 0);
__decorate([
    observable
], FilterSelect.prototype, "filterText", void 0);
__decorate([
    computed
], FilterSelect.prototype, "value", null);
__decorate([
    autobind
], FilterSelect.prototype, "on", null);
__decorate([
    action
], FilterSelect.prototype, "setText", null);
__decorate([
    autobind
], FilterSelect.prototype, "getRootDomNode", null);
__decorate([
    autobind
], FilterSelect.prototype, "defaultRenderer", null);
__decorate([
    autobind
], FilterSelect.prototype, "handleDataSetReset", null);
__decorate([
    autobind,
    action
], FilterSelect.prototype, "handleDataSetUpdate", null);
__decorate([
    autobind
], FilterSelect.prototype, "handleBlur", null);
__decorate([
    autobind
], FilterSelect.prototype, "handleFieldChange", null);
__decorate([
    autobind
], FilterSelect.prototype, "handleInput", null);
__decorate([
    autobind
], FilterSelect.prototype, "handleFieldEnterDown", null);
__decorate([
    autobind
], FilterSelect.prototype, "handleKeyDown", null);
__decorate([
    autobind
], FilterSelect.prototype, "handleEnterDown", null);
__decorate([
    action
], FilterSelect.prototype, "setSelectField", null);
__decorate([
    action
], FilterSelect.prototype, "setQueryValue", null);
__decorate([
    action
], FilterSelect.prototype, "clear", null);
__decorate([
    autobind
], FilterSelect.prototype, "handleMouseDown", null);
FilterSelect = __decorate([
    observer
], FilterSelect);
export default FilterSelect;
